import type { AxiosResponse } from "axios";
import { reportError, reportMessage } from "@/utils/sentry";
import { api } from "@/api";
import { jwtService } from "@/services/jwt";
import type {
  LoginResponse,
  TokenRefreshResponse,
  HeartBeatResponse,
  UserResponse,
  CodeResponse,
  GetUserResponse,
  PasskeyRegistrationOptions,
  PasskeyRegistrationCredential,
  PasskeyRegistrationResponse,
  PasskeyAuthenticationOptions,
  PasskeyAuthenticationCredential,
  PasskeyAuthenticationResponse,
} from "@/types/user";
import type {
  JumpUrlResponse,
  JumpUrlDetailResponse,
  JumpUrlUpdateRequest,
  PasswordChangeStatusResponse,
} from "@/types/jumpUrl";

// 使用统一的API实例，无需重复定义拦截器

// 登录
export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  try {
    const response: AxiosResponse = await api.post("login/", {
      username,
      password,
    });

    // 记录成功登录事件，包含更多用户信息
    reportMessage(`用户 ${username} 登录成功`, "info", {
      username: response.data.username,
      name: response.data.name,
      action: "login",
      timestamp: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    // 登录失败，但不记录密码
    reportError(error as Error, {
      type: "auth_error",
      action: "login",
      username,
      timestamp: new Date().toISOString(),
      // 不记录密码
    });
    throw error;
  }
}

// 获取token
export async function tokenObtain(): Promise<LoginResponse> {
  try {
    const response: AxiosResponse = await api.get("token_obtain/");
    return response.data;
  } catch (error) {
    // 记录token获取失败
    reportError(error as Error, {
      type: "auth_error",
      action: "token_obtain",
    });
    throw error;
  }
}

// 刷新token
export async function refreshToken(
  refresh: string,
): Promise<TokenRefreshResponse> {
  try {
    const response: AxiosResponse = await api.post("token_refresh/", {
      refresh,
    });
    return response.data;
  } catch (error) {
    // 记录token刷新失败
    reportError(error as Error, {
      type: "auth_error",
      action: "refresh_token",
    });
    throw error;
  }
}

// 获取Passkey认证后的JWT令牌
export async function getPasskeyToken(userId: string): Promise<LoginResponse> {
  try {
    // 清理用户ID，去除可能的额外字符
    const cleanUserId = userId.trim().replace(/\/+$/, "");

    // 构建URL并记录日志
    const url = `loginPasskey/?userId=${encodeURIComponent(cleanUserId)}`;
    console.log("获取Passkey令牌URL:", url);

    const response: AxiosResponse = await api.get(url);
    console.log("Passkey令牌响应:", response.data);

    // 记录成功获取令牌事件
    reportMessage(`用户 ${cleanUserId} 通过Passkey获取令牌成功`, "info", {
      userId: cleanUserId,
      action: "passkey_get_token",
      timestamp: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "get_passkey_token",
      userId,
    });
    throw error;
  }
}

// 获取服务器状态
export async function getHeartBeat(): Promise<HeartBeatResponse> {
  const response: AxiosResponse = await api.get("heartbeat/");
  return response.data;
}

// 获取用户信息
export async function getUser(username: string): Promise<UserResponse> {
  const response: AxiosResponse = await api.get(`users/${username}/`);
  return response.data;
}

// 获取所有用户信息
export async function getAllUsers(
  page: number,
  pageSize: number,
): Promise<UserResponse[]> {
  const response: AxiosResponse = await api.get("users/", {
    params: {
      page,
      page_size: pageSize,
    },
  });
  return response.data;
}

/**
 * 获取当前用户的 SSO 信息
 * 注意：后端会对敏感信息进行脱敏处理
 *
 * @returns {Promise<GetUserResponse>} 返回用户信息对象
 *
 * @example
 * // 返回数据示例
 * {
 *   "username": "20*******86",    // 脱敏的学号/工号
 *   "name": "黄**",              // 脱敏的姓名
 *   "grade": "2023",            // 年级
 *   "major_class": "软件工程2班", // 专业班级
 *   "email": "a1*******11@163.com", // 脱敏的邮箱
 *   "phone_number": "134****6007",  // 脱敏的手机号
 *   "is_active": true,          // 是否激活
 *   "is_staff": false,          // 是否为教职工
 *   "is_superuser": false,      // 是否为超级管理员
 *   "message": "Get User success", // 接口返回信息
 *   "role": "学生"               // 用户角色
 * }
 */
export async function getSSOUser(): Promise<GetUserResponse> {
  const response: AxiosResponse = await api.get(`GetUser/`);
  return response.data;
}

// 修改用户字段
export async function modifyField(
  field: string,
  value: string,
): Promise<UserResponse> {
  const response: AxiosResponse = await api.post(`modify/${field}/`, {
    [field]: value,
  });
  return response.data;
}

// 修改邮箱
export async function modifyEmail(email: string): Promise<UserResponse> {
  return modifyField("email", email);
}

// 修改密码
export async function modifyPassword(
  oldPassword: string,
  newPassword: string,
): Promise<UserResponse> {
  try {
    const response: AxiosResponse = await api.post("modify/password/", {
      old_password: oldPassword,
      new_password: newPassword,
    });

    // 记录密码修改成功，包含更多上下文
    const user = jwtService.parseToken(jwtService.getToken() || "");
    reportMessage("密码修改成功", "info", {
      username: user?.username,
      name: user?.name,
      action: "modify_password",
      timestamp: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    // 记录密码修改失败，但不记录密码内容
    const user = jwtService.parseToken(jwtService.getToken() || "");
    reportError(error as Error, {
      type: "auth_error",
      action: "modify_password",
      username: user?.username,
      name: user?.name,
      timestamp: new Date().toISOString(),
      // 不记录密码
    });
    throw error;
  }
}

// 重置密码
export async function resetPassword(
  username: string,
  code: string,
  newPassword: string,
): Promise<UserResponse> {
  try {
    const response: AxiosResponse = await api.post("reset/password/", {
      username,
      code,
      new_password: newPassword,
    });

    // 记录密码重置成功
    reportMessage(`用户 ${username} 密码重置成功`, "info");

    return response.data;
  } catch (error) {
    // 记录密码重置失败，但不记录密码内容
    reportError(error as Error, {
      type: "auth_error",
      action: "reset_password",
      username,
      // 不记录密码和验证码
    });
    throw error;
  }
}

// 请求密码重置
export async function requestPasswordReset(
  username: string,
  email: string,
): Promise<CodeResponse> {
  try {
    const response: AxiosResponse = await api.post("code/", {
      username,
      email,
    });

    // 记录密码重置请求成功
    reportMessage(`用户 ${username} 请求密码重置`, "info");

    return response.data;
  } catch (error) {
    // 记录密码重置请求失败
    reportError(error as Error, {
      type: "auth_error",
      action: "request_password_reset",
      username,
      email,
    });
    throw error;
  }
}

// 验证Token
export async function verifyToken(token: string): Promise<void> {
  await api.post("token_verify/", { token });
}

// Passkey相关API

// 获取注册选项
export async function getPasskeyRegistrationOptions(): Promise<PasskeyRegistrationOptions> {
  try {
    const response: AxiosResponse = await api.post("register/begin/");
    console.log("Register options response:", response.data);
    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "get_passkey_registration_options",
    });
    throw error;
  }
}

// 完成注册过程
export async function finishPasskeyRegistration(
  registerData: PasskeyRegistrationCredential,
): Promise<PasskeyRegistrationResponse> {
  try {
    const response: AxiosResponse = await api.post(
      "register/finish/",
      registerData,
    );
    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "finish_passkey_registration",
    });
    throw error;
  }
}

// 获取认证选项
export async function getPasskeyAuthenticationOptions(
  userId: string,
): Promise<PasskeyAuthenticationOptions> {
  try {
    // 清理用户ID，去除可能的额外字符
    const cleanUserId = userId.trim().replace(/\/+$/, "");

    // 构建URL并记录日志
    const url = `auth/begin/?userId=${encodeURIComponent(cleanUserId)}`;
    console.log("认证请求URL:", url);

    const response: AxiosResponse = await api.get(url);
    console.log("Authentication options response:", response.data);
    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "get_passkey_authentication_options",
      userId,
    });
    throw error;
  }
}
// 完成认证过程
export async function finishPasskeyAuthentication(
  loginData: PasskeyAuthenticationCredential & { userId: string },
): Promise<PasskeyAuthenticationResponse> {
  try {
    const response: AxiosResponse = await api.post("auth/finish/", loginData);

    // 记录成功登录事件
    reportMessage(`用户 ${loginData.userId} 使用Passkey登录成功`, "info", {
      userId: loginData.userId,
      action: "passkey_login",
      timestamp: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "finish_passkey_authentication",
      userId: loginData.userId,
    });
    throw error;
  }
}

// WebAuthn助手函数

// 转换ArrayBuffer为Base64编码的字符串
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  // 使用btoa进行Base64编码
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// 将Base64编码的字符串转换为ArrayBuffer
export function base64ToArrayBuffer(base64URL: string): ArrayBuffer {
  if (!base64URL) {
    throw new Error("无效的Base64字符串");
  }
  const base64 = base64URL.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64.padEnd(base64.length + padLen, "=");
  const binary = atob(padded);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return buffer;
}

// 获取跳转URL
export async function getJumpUrl(alias: string): Promise<JumpUrlResponse> {
  try {
    const response: AxiosResponse = await api.get(`jumpUrl/${alias}/`);
    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "get_jump_url",
      alias,
    });
    throw error;
  }
}

// 更新跳转URL
export async function updateJumpUrl(
  alias: string,
  data: JumpUrlUpdateRequest,
): Promise<JumpUrlDetailResponse> {
  try {
    const response: AxiosResponse = await api.put(`jumpUrl/${alias}/`, data);
    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "update_jump_url",
      alias,
    });
    throw error;
  }
}

// 检查密码是否已修改
export async function checkPasswordChanged(): Promise<PasswordChangeStatusResponse> {
  try {
    const response: AxiosResponse = await api.get("isPasswordChanged/");
    return response.data;
  } catch (error) {
    reportError(error as Error, {
      type: "auth_error",
      action: "check_password_changed",
    });
    throw error;
  }
}

// auth.ts 不再导出独立的 api 实例，使用统一的 api 实例
